// The status button in the toolbar (it used to be a dot beside the logo) and its popover.
//
// Six states, each with its own shape so colour is never the only signal: connecting (a dotted
// ring), good (a check), good but switched for this site (a check in a ring), reconnecting (!),
// our tunnel or server coming back (! in amber, pages wait or go another way meanwhile) and
// offline (a slash). A word shows beside it only when something is wrong. Real changes are spoken
// once; the 1.5 s poll never talks. "Status button" off in Settings hides it while things are fine.
import { get, subscribe } from "../shared/settings-store.js";
import { ENGINE_CHOICES, engineLabel } from "../shared/settings-schema.js";

const G = {
	connecting: '<circle cx="8" cy="8" r="5.2" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="2.2 2.6"/>',
	ok: '<circle cx="8" cy="8" r="6.5" fill="currentColor"/><path d="M5.2 8.2l1.9 1.9 3.8-4" fill="none" stroke="var(--status-on)" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
	auto: '<circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.3"/><circle cx="8" cy="8" r="4.6" fill="currentColor"/><path d="M6.1 8.1l1.3 1.3 2.6-2.7" fill="none" stroke="var(--status-on)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
	down: '<circle cx="8" cy="8" r="6.5" fill="currentColor"/><path d="M8 4.6v4.2" stroke="var(--status-on)" stroke-width="1.8" stroke-linecap="round"/><circle cx="8" cy="11.2" r="1" fill="var(--status-on)"/>',
	wait: '<circle cx="8" cy="8" r="6.5" fill="currentColor"/><path d="M8 4.6v4.2" stroke="var(--status-on)" stroke-width="1.8" stroke-linecap="round"/><circle cx="8" cy="11.2" r="1" fill="var(--status-on)"/>',
	offline: '<circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M3.8 12.2l8.4-8.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
};
const WORD = { connecting: "Connecting", ok: "Good", auto: "Good", down: "Reconnecting", wait: "Reconnecting", offline: "Offline" };

let C = null, btn = null, pop = null, last = "", lastSaid = "", paintedState = "";

// The state right now: connecting, ok, auto (good, switched for this site), down, wait or offline.
export function statusState() {
	if (!C) return "connecting";
	const cs = C.connState ? C.connState() : "";
	if (navigator.onLine === false || cs === "offline") return "offline";
	if (!C.status.ready) return "connecting";
	if (cs === "wait") return "wait";
	if (!C.status.healthy) return "down";
	return C.connectionTitle(C.active()).auto ? "auto" : "ok";
}
// What the button is called: "Connection: good. Standard · Stable".
export function statusName(st = statusState()) {
	if (st === "ok" || st === "auto") {
		const d = C.connectionTitle(C.active());
		return "Connection: good. " + String(d.title || "").replace(/\n/g, ". ");
	}
	return "Connection: " + WORD[st].toLowerCase();
}
export const statusGlyph = (st) => '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">' + G[st] + "</svg>";

function paint() {
	if (!btn) return;
	const st = statusState();
	const name = statusName(st);
	btn.dataset.state = st;
	btn.setAttribute("aria-label", name);
	btn.title = name;
	if (btn.dataset.glyph !== st) { btn.querySelector(".rv-status-glyph").innerHTML = statusGlyph(st); btn.dataset.glyph = st; }
	// The word shows beside the glyph only when something is wrong, and always in the phone tray.
	btn.querySelector(".rv-status-text").textContent = st === "ok" || st === "auto" ? "Connection good" : WORD[st];
	// "Status button" off hides it only while things are fine.
	const fine = st === "ok" || st === "auto" || st === "connecting";
	btn.hidden = get("showStatus") === false && fine;
	dock(fine);
	// Say real changes once: good -> reconnecting -> good, never a repeat.
	const bucket = st === "auto" ? "ok" : st === "wait" ? "down" : st;
	if (last && bucket !== last && bucket !== "connecting" && bucket !== lastSaid) {
		C.announce(bucket === "ok" ? "Connection is back." : bucket === "down" ? "Connection lost. Re:Vault is trying again." : "You're offline.");
		lastSaid = bucket;
	}
	if (bucket !== "connecting") last = bucket;
	if (st !== paintedState) { paintedState = st; if (C.changed) C.changed(st); }
	if (pop && !pop.hidden && st !== popState) fill();
}

// Phones: while things are fine the button waits in the More tray, so the address bar keeps its
// room. When something goes wrong it moves out beside the address bar, where it is seen.
const phoneMq = typeof matchMedia === "function" ? matchMedia("(max-width: 599px)") : null;
function dock(fine) {
	const tray = document.getElementById("rv-nav-more"), more = document.getElementById("rv-more");
	if (!tray || !more) return;
	const inTray = !!(phoneMq && phoneMq.matches && fine);
	if (inTray && btn.parentElement !== tray) tray.prepend(btn);
	else if (!inTray && btn.parentElement === tray) more.before(btn);
}
const shown = (el) => !!(el && el.isConnected && el.getClientRects().length);

// --- the popover --------------------------------------------------------------------------------
const h = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
let from = null, popState = "";
function fill() {
	const st = statusState();
	popState = st;
	// keep focus on the same button when the content is redrawn
	const had = [...pop.querySelectorAll("button")].indexOf(document.activeElement);
	pop.textContent = "";
	const title = h("h2", "rv-statp-h", st === "ok" || st === "auto" ? "Connection: good" : "Connection: " + WORD[st].toLowerCase());
	title.id = "rv-statp-h";
	pop.append(title);
	if (st === "ok" || st === "auto") {
		const d = C.connectionTitle(C.active());
		const lines = String(d.title || "").split("\n");
		pop.append(h("p", "rv-statp-p", /switched|for this page|Your setting/.test(lines[0]) ? lines[0] + "." : lines[0].replace(/ · /, " engine, ") + " connection."));
		if (lines[1]) pop.append(h("p", "rv-statp-p", lines[1]));
		pop.append(h("p", "rv-statp-p rv-statp-muted", "Encrypted from your browser."));
	} else if (st === "connecting") {
		pop.append(h("p", "rv-statp-p", "Getting ready. Pages open in a moment."));
	} else if (st === "wait") {
		pop.append(h("p", "rv-statp-p", "Re:Vault's connection is coming back. Pages wait a moment or open another way meanwhile."));
	} else {
		pop.append(h("p", "rv-statp-p", st === "offline" ? "This device is offline. Pages will open again when the connection is back." : "Pages will open again when the connection is back. Re:Vault is trying again."));
	}
	const t = C.active();
	if (t && t.url && t.src && !t.internal && (st === "ok" || st === "auto")) {
		const sec = h("div", "rv-statp-sec");
		sec.append(h("h3", "rv-statp-h3", "This page"), h("p", "rv-statp-p", "Opened with " + engineLabel(t.engine || get("engine")) + ". If it looks broken, reload it with:"));
		const row = h("div", "rv-statp-row");
		for (const [v, label] of ENGINE_CHOICES) {
			if (v === t.engine) continue;
			const b = h("button", "rv-btn", label);
			b.type = "button";
			b.addEventListener("click", () => { close(false); C.reloadWithEngine(t.id, v); });
			row.append(b);
		}
		sec.append(row);
		pop.append(sec);
	}
	const foot = h("div", "rv-statp-row rv-statp-foot");
	const page = h("button", "rv-btn", st === "ok" || st === "auto" ? "Open the status page" : "Check the status page");
	page.type = "button";
	page.addEventListener("click", () => { close(false); C.openInternal("/status", "Status"); });
	const eng = h("button", "rv-btn", "Engine settings");
	eng.type = "button";
	eng.addEventListener("click", () => { close(false); C.openSettings("#set=engine"); });
	foot.append(page, eng);
	pop.append(foot);
	if (had >= 0) { const b = pop.querySelectorAll("button"); (b[had] || b[b.length - 1]).focus(); }
}
function place(anchor) {
	const r = anchor.getBoundingClientRect();
	const w = pop.offsetWidth || 320;
	const below = r.top < innerHeight / 2;
	pop.style.left = Math.max(8, Math.min(r.right - w, innerWidth - w - 8)) + "px";
	if (below) { pop.style.top = r.bottom + 6 + "px"; pop.style.bottom = ""; }
	else { pop.style.bottom = innerHeight - r.top + 6 + "px"; pop.style.top = ""; }
}
function close(restore = true) {
	if (!pop || pop.hidden) return;
	pop.hidden = true;
	if (from) from.setAttribute("aria-expanded", "false");
	if (restore && from) { if (shown(from)) from.focus(); else { const m = document.getElementById("rv-more"); if (shown(m)) m.focus(); } }
	from = null;
}
// Open the popover under a button (the toolbar's, the island's).
export function openStatus(anchor) {
	if (!pop) {
		pop = h("div", "rv-statp");
		pop.id = "rv-statp";
		pop.setAttribute("role", "dialog");
		pop.setAttribute("aria-labelledby", "rv-statp-h");
		pop.hidden = true;
		document.body.append(pop);
		if (btn) btn.setAttribute("aria-controls", pop.id);
		document.addEventListener("pointerdown", (e) => { if (!pop.hidden && !pop.contains(e.target) && !(from && from.contains(e.target))) close(false); }, true);
		document.addEventListener("keydown", (e) => { if (e.key === "Escape" && !pop.hidden) close(); });
		pop.addEventListener("keydown", (e) => {
			if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); return; }
			if (e.key !== "Tab") return;
			const items = [...pop.querySelectorAll("button")];
			if (!items.length) return;
			const first = items[0], end = items[items.length - 1];
			if (e.shiftKey && document.activeElement === first) { e.preventDefault(); end.focus(); }
			else if (!e.shiftKey && document.activeElement === end) { e.preventDefault(); first.focus(); }
		});
		addEventListener("resize", () => close(false));
	}
	if (!pop.hidden && from === anchor) { close(); return; }
	from = anchor;
	fill();
	pop.hidden = false;
	// from the phone tray, which closes as this opens: sit under the More button instead
	const more = document.getElementById("rv-more");
	place(anchor.closest("#rv-nav-more") && shown(more) ? more : anchor);
	anchor.setAttribute("aria-expanded", "true");
	const b = pop.querySelector("button");
	if (b) b.focus();
}

export function initStatus(ctx) {
	C = ctx;
	btn = document.getElementById("rv-status");
	if (btn) {
		btn.addEventListener("click", () => openStatus(btn));
	}
	paint();
	setInterval(paint, 1500);
	addEventListener("offline", paint);
	if (phoneMq) phoneMq.addEventListener("change", paint);
	subscribe((_s, d) => { if (!d || !d.keys || d.keys.includes("showStatus")) paint(); });
	return paint;
}
